if (!customElements.get('ath-home-browser')) {
  customElements.define(
    'ath-home-browser',
    class AthHomeBrowser extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';
        this.storageKey = 'athHomeBrowseTab';
        this.tabs = Array.from(this.querySelectorAll('[data-ath-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-ath-panel]'));
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(tab.dataset.athTab, true));
          tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
        });
        const savedTab = window.sessionStorage.getItem(this.storageKey);
        if (savedTab && this.tabs.some((tab) => tab.dataset.athTab === savedTab)) {
          this.select(savedTab, false);
        }
      }

      select(name, moveFocus) {
        this.tabs.forEach((tab) => {
          const selected = tab.dataset.athTab === name;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          if (selected && moveFocus) tab.focus({ preventScroll: true });
        });
        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.athPanel !== name;
        });
        window.sessionStorage.setItem(this.storageKey, name);
      }

      onKeydown(event, index) {
        const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        this.select(this.tabs[next].dataset.athTab, true);
      }
    }
  );
}

if (!customElements.get('ath-home-finder')) {
  customElements.define(
    'ath-home-finder',
    class AthHomeFinder extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';
        this.stages = Array.from(this.querySelectorAll('[data-ath-finder-stage]'));
        this.progress = Array.from(this.querySelectorAll('[data-ath-progress]'));
        this.typeLinks = Array.from(this.querySelectorAll('[data-ath-finder-type]'));
        this.optionsContainer = this.querySelector('[data-ath-finder-options]');
        this.question = this.querySelector('[data-ath-finder-question]');
        this.resultLink = this.querySelector('[data-ath-finder-result]');
        this.resultHeading = this.querySelector('[data-ath-finder-result-heading]');
        this.resultCopy = this.querySelector('[data-ath-finder-result-copy]');
        this.selectedType = null;
        this.selectedTypeLink = null;
        this.selectedOption = null;
        this.storageKey = 'athHomeFinderState';
        this.finderOptions = {
          intermittent: {
            question: 'Which intermittent catheter detail was specified?',
            options: [
              {
                title: 'Straight tip',
                url: '/collections/straight-tip-intermittent-catheters',
                copy: 'Browse single-use intermittent catheters with a straight tip.'
              },
              {
                title: 'Coudé tip',
                url: '/collections/coude-tip-intermittent-catheter',
                copy: 'Browse intermittent catheters with a curved Coudé tip. Use this option only when that tip style was specified for you.'
              },
              {
                title: 'Hydrophilic or pre-lubricated',
                url: '/collections/hydrophilic-intermittent-catheters',
                copy: 'Browse hydrophilic intermittent catheters designed with a lubricated surface.'
              },
              {
                title: 'Browse all intermittent',
                url: '/collections/intermittent-catheters',
                copy: 'See all intermittent catheters, then filter by the details on your current product or prescription.'
              }
            ]
          },
          closed: {
            question: 'Which closed-system setup do you use?',
            options: [
              {
                title: 'With insertion supplies',
                url: '/collections/closed-system-catheters-with-insertion-supplies',
                copy: 'Browse closed-system catheter kits packaged with insertion supplies.'
              },
              {
                title: 'Browse closed-system catheters',
                url: '/collections/closed-system-catheters',
                copy: 'See all no-touch closed-system catheter options and confirm the included components.'
              },
              {
                title: 'I need product guidance',
                url: '/pages/contact-us',
                copy: 'A product specialist can help you find items that match the details you already use.',
                actionLabel: 'Contact a product specialist'
              }
            ]
          },
          external: {
            question: 'Which external-catheter detail are you matching?',
            options: [
              {
                title: 'Browse external catheters',
                url: '/collections/external-catheters',
                copy: 'See all external catheter styles and compare the product details.'
              },
              {
                title: 'Extra or extended adhesive',
                url: '/pages/search-results-page?q=extra%20adhesive%20external%20catheter',
                copy: 'Browse external catheters described with extra or extended adhesive.'
              },
              {
                title: 'Short or sport length',
                url: '/pages/search-results-page?q=sport%20external%20catheter',
                copy: 'Browse short and sport-length external catheter options.'
              },
              {
                title: 'Holders and securement',
                url: '/collections/external-catheter-holders-strips',
                copy: 'Browse external-catheter holders and securement strips.'
              }
            ]
          },
          foley: {
            question: 'What Foley-related product are you shopping for?',
            options: [
              {
                title: 'Foley catheters',
                url: '/collections/foley-indwelling-catheters',
                copy: 'Browse indwelling Foley catheters and confirm every prescribed specification.'
              },
              {
                title: 'Insertion kits and trays',
                url: '/collections/foley-catheter-insertion-kits-trays',
                copy: 'Browse Foley catheter insertion kits and trays.'
              },
              {
                title: 'Drainage bags',
                url: '/collections/urinary-drainage-bags',
                copy: 'Browse urinary drainage bags and related collection systems.'
              },
              {
                title: 'Holders and straps',
                url: '/collections/foley-catheter-straps',
                copy: 'Browse catheter holders and securement straps.'
              }
            ]
          }
        };

        this.typeLinks.forEach((link) => {
          link.addEventListener('click', (event) => {
            event.preventDefault();
            this.selectedType = { key: link.dataset.typeKey, title: link.dataset.title, url: link.href };
            this.selectedTypeLink = link;
            this.selectedOption = null;
            this.renderOptions();
            this.showStage(2, true);
            this.persistState(2);
          });
        });
        this.optionsContainer?.addEventListener('click', (event) => {
          const button = event.target.closest('[data-ath-finder-option]');
          if (!button) return;
          this.showResult({
            title: button.dataset.optionTitle,
            url: button.dataset.optionUrl,
            copy: button.dataset.optionCopy,
            actionLabel: button.dataset.optionAction || 'View matching products'
          });
        });
        this.querySelector('[data-ath-finder-back]')?.addEventListener('click', () => {
          this.showStage(1, false);
          this.persistState(1);
          this.selectedTypeLink?.focus({ preventScroll: true });
        });
        this.querySelector('[data-ath-finder-restart]')?.addEventListener('click', () => this.reset());

        this.restoreState();
      }

      showStage(stageNumber, moveFocus = true) {
        this.stages.forEach((stage) => {
          stage.hidden = Number(stage.dataset.athFinderStage) !== stageNumber;
        });
        this.progress.forEach((item) => {
          item.classList.toggle('is-active', Number(item.dataset.athProgress) === stageNumber);
          item.classList.toggle('is-complete', Number(item.dataset.athProgress) < stageNumber);
        });
        const heading = this.querySelector(`[data-ath-finder-stage="${stageNumber}"] h3`);
        if (heading && moveFocus) {
          heading.tabIndex = -1;
          heading.focus({ preventScroll: true });
        }
      }

      renderOptions() {
        const config = this.finderOptions[this.selectedType?.key];
        if (!config || !this.optionsContainer) return;
        if (this.question) this.question.textContent = config.question;
        this.optionsContainer.replaceChildren(...config.options.map((option) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.athFinderOption = '';
          button.dataset.optionTitle = option.title;
          button.dataset.optionUrl = option.url;
          button.dataset.optionCopy = option.copy;
          button.dataset.optionAction = option.actionLabel || 'View matching products';
          button.textContent = option.title;
          return button;
        }));
      }

      showResult(option) {
        if (!this.selectedType) return;
        this.selectedOption = option;
        this.resultLink.href = option.url;
        this.resultLink.textContent = option.actionLabel || 'View matching products';
        this.resultHeading.textContent = option.title;
        this.resultCopy.textContent = `${option.copy} Confirm the type, tip, size, length, and other prescribed details before ordering.`;
        this.showStage(3, true);
        this.persistState(3);
      }

      reset() {
        this.selectedType = null;
        this.selectedTypeLink = null;
        this.selectedOption = null;
        window.sessionStorage.removeItem(this.storageKey);
        this.showStage(1, false);
        this.typeLinks[0]?.focus({ preventScroll: true });
      }

      persistState(stage) {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify({
          stage,
          typeKey: this.selectedType?.key || null,
          typeTitle: this.selectedType?.title || null,
          typeUrl: this.selectedType?.url || null,
          option: this.selectedOption
        }));
      }

      restoreState() {
        let state;
        try {
          state = JSON.parse(window.sessionStorage.getItem(this.storageKey));
        } catch (_error) {
          state = null;
        }
        if (!state || !Number.isInteger(state.stage)) return;

        if (state.typeUrl) {
          this.selectedTypeLink = this.typeLinks.find((link) => link.href === state.typeUrl) || null;
          this.selectedType = {
            key: state.typeKey || this.selectedTypeLink?.dataset.typeKey,
            title: state.typeTitle || this.selectedTypeLink?.dataset.title || 'Recommended products',
            url: state.typeUrl
          };
        }

        if (state.stage === 3 && this.selectedType && state.option?.url) {
          this.selectedOption = state.option;
          this.resultLink.href = state.option.url;
          this.resultLink.textContent = state.option.actionLabel || 'View matching products';
          this.resultHeading.textContent = state.option.title;
          this.resultCopy.textContent = `${state.option.copy} Confirm the type, tip, size, length, and other prescribed details before ordering.`;
          this.showStage(3, false);
          return;
        }

        if (this.selectedType) this.renderOptions();
        this.showStage(state.stage === 2 && this.selectedType ? 2 : 1, false);
      }
    }
  );
}

if (!customElements.get('ath-home-products')) {
  customElements.define(
    'ath-home-products',
    class AthHomeProducts extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';

        const imageMap = new Map(
          [...this.querySelectorAll('[data-ath-product-image][data-ath-product-handle]')].map((item) => [
            item.dataset.athProductHandle,
            { src: item.dataset.athProductImage, alt: item.dataset.athProductAlt }
          ])
        );
        const cards = [...this.querySelectorAll('.card-wrapper')];

        // Keep a row visually consistent. Curated PDP media is used only when
        // every featured product has an approved replacement; otherwise all
        // cards retain their native Shopify featured images.
        if (cards.length === 0 || imageMap.size !== cards.length) return;

        const replacements = cards.map((card) => {
          const productLink = card.querySelector('.card__heading a[href*="/products/"]');
          const match = productLink?.pathname.match(/\/products\/([^/?#]+)/);
          const image = card.querySelector('.card__media img');
          const curated = match ? imageMap.get(match[1]) : null;
          return curated && image ? { image, curated } : null;
        });

        if (replacements.some((replacement) => replacement === null)) return;

        const withWidth = (src, width) => `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
        replacements.forEach(({ image, curated }) => {
          image.src = withWidth(curated.src, 640);
          image.srcset = [240, 360, 480, 640].map((width) => `${withWidth(curated.src, width)} ${width}w`).join(', ');
          image.sizes = '(min-width: 750px) 25vw, 50vw';
          image.alt = curated.alt;
          image.dataset.athCuratedImage = curated.src;
        });
      }
    }
  );
}

(() => {
  const home = document.querySelector('.ath-home');
  if (!home) return;

  // Keep collection landing order explicit without managing scroll position.
  // Native browser restoration is more reliable across first load, Safari,
  // Shopify's preview bar, and back/forward cache restores.
  home.querySelectorAll('a[href]').forEach((link) => {
    let url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch (_error) {
      return;
    }
    if (url.origin !== window.location.origin || !/^\/collections\/[^/]+/.test(url.pathname)) return;
    if (!url.searchParams.has('sort_by')) url.searchParams.set('sort_by', 'best-selling');
    link.href = url.toString();
  });
})();

